import { useState, useEffect } from 'react';
import { Shield, Check, X, Lock, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { AdminRole, ROLE_PERMISSIONS, AdminPermissions } from '@/types';
import { hasPermission } from '@/lib/permissions';
import { useNavigate } from 'react-router-dom';

interface AdminUser {
  id: string;
  email: string;
  admin_role: AdminRole;
  created_at: string;
}

export function PermissionsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<AdminRole | null>(null);
  const [editedPermissions, setEditedPermissions] = useState<Record<AdminRole, AdminPermissions>>({
    ...ROLE_PERMISSIONS
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role, admin_role')
        .eq('id', user.id)
        .single();

      if (!profile?.role || profile.role !== 'admin') {
        navigate('/dashboard');
        toast.error('Unauthorized access');
        return;
      }

      if (!hasPermission(profile.admin_role as AdminRole, 'editPermissions')) {
        navigate('/admin');
        toast.error('Insufficient permissions');
        return;
      }

      setCurrentUserRole(profile.admin_role as AdminRole);
      fetchAdminUsers();
    } catch (error) {
      console.error('Error checking access:', error);
      navigate('/dashboard');
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, admin_role, created_at')
        .eq('role', 'admin')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAdminUsers(data || []);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: AdminRole) => {
    try {
      // Only founders can change roles
      if (!hasPermission(currentUserRole!, 'editRoles')) {
        toast.error('Only founders can modify roles');
        return;
      }

      // Prevent changing own role
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id === userId) {
        toast.error('Cannot modify your own role');
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ admin_role: newRole })
        .eq('id', userId);

      if (error) throw error;

      toast.success('Admin role updated successfully');
      fetchAdminUsers();
    } catch (error) {
      console.error('Error updating admin role:', error);
      toast.error('Failed to update admin role');
    }
  };

  const handlePermissionChange = (role: AdminRole, permission: keyof AdminPermissions, value: boolean) => {
    // Don't allow editing founder permissions
    if (role === 'founder') {
      toast.error("Founder permissions cannot be modified");
      return;
    }

    setEditedPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [permission]: value
      }
    }));
  };

  const handleSavePermissions = async () => {
    try {
      setSaving(true);

      // Update the ROLE_PERMISSIONS object
      Object.assign(ROLE_PERMISSIONS, editedPermissions);

      toast.success('Permissions updated successfully');
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeColor = (role: AdminRole) => {
    switch (role) {
      case 'founder':
        return 'bg-purple-500';
      case 'admin':
        return 'bg-blue-500';
      case 'moderator':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Permissions Management</h1>
          <p className="text-muted-foreground">Manage admin roles and permissions</p>
        </div>
        <div className="space-x-2">
          <Badge variant="secondary" className="text-sm">
            <Lock className="h-4 w-4 mr-1" />
            {currentUserRole?.toUpperCase()}
          </Badge>
          {hasPermission(currentUserRole!, 'editPermissions') && (
            <Button 
              onClick={handleSavePermissions} 
              disabled={saving || currentUserRole !== 'founder'}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          )}
        </div>
      </div>

      {/* Role Permissions Matrix */}
      <Card>
        <CardHeader>
          <CardTitle>Role Permissions Matrix</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Permission</TableHead>
                <TableHead>Founder</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Moderator</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(editedPermissions.founder).map(([permission, _]) => (
                <TableRow key={permission}>
                  <TableCell className="font-medium">
                    {permission.replace(/([A-Z])/g, ' $1').trim()}
                  </TableCell>
                  {(['founder', 'admin', 'moderator'] as AdminRole[]).map((role) => (
                    <TableCell key={role}>
                      <Switch
                        checked={editedPermissions[role][permission as keyof AdminPermissions]}
                        onCheckedChange={(checked) => 
                          handlePermissionChange(role, permission as keyof AdminPermissions, checked)
                        }
                        disabled={role === 'founder' || currentUserRole !== 'founder'}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Admin Users */}
      <Card>
        <CardHeader>
          <CardTitle>Admin Users</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {adminUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge className={getRoleBadgeColor(user.admin_role)}>
                      {user.admin_role?.toUpperCase() || 'NO ROLE'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Select
                      value={user.admin_role}
                      onValueChange={(value: AdminRole) => handleRoleChange(user.id, value)}
                      disabled={!hasPermission(currentUserRole!, 'editRoles')}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="founder">Founder</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="moderator">Moderator</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
